import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo, Button, Callout, TextField } from "@/design/components";
import { ApiError } from "@/api/client";
import { useOperatorAuth } from "@/app/operator/operatorAuth";
import styles from "./OperatorLoginScreen.module.css";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});
type Values = z.infer<typeof schema>;

/** Operator (platform-staff) sign in — a deliberately plain, internal surface (no
 *  public marketing), distinct from the tenant portal login. */
export function OperatorLoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useOperatorAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const navState = location.state as { from?: string; expired?: boolean } | null;
  const from = navState?.from ?? "/operator/app";
  const [sessionExpired] = useState(() => navState?.expired === true);

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      await signIn(v.email, v.password);
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not sign in. Please try again.");
    }
  });

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.head}>
          <Logo size={28} />
          <span className={styles.badge}>Operator</span>
        </div>
        <h1 className={styles.title}>Platform operator sign in</h1>
        <p className={styles.sub}>Staff access to the Cogent control plane.</p>

        {sessionExpired && !formError && (
          <div className={styles.notice}>
            <Callout tone="warning" title="Your session expired">Sign in again to continue.</Callout>
          </div>
        )}

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {formError && (
            <div role="alert">
              <Callout tone="danger">{formError}</Callout>
            </div>
          )}
          <TextField
            label="Operator email"
            type="email"
            autoComplete="email"
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            error={errors.password?.message}
            {...register("password")}
          />
          <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
